import { observable, action, IObservableArray } from "mobx";
import { hashHistory } from "react-router";
import SeekerStore from "./seekerStore";
import OpportunityStore from "./opportunityStore";
import { ProfileSkillState, ProfileViewState, JobType, EmptyId, ExperienceType, EdType, SavedOpportunityState, BusyEvent, AddExpRoute, CertValidation } from "../models/enums";
import { ParsedResumeResults } from "../models/parsedResumeResults";
import { SkillSuggestion } from "../models/skills";
import { IInMemLookup } from "../models/lookup";
import { Profile, Demographics, Referral, SavedOpportunity, IExperience, WorkExperience, ActivityExperience, EducationExperience, CCLCExperience, DeleteExperience, Language, ISkillSource, ExperienceSkill, ProfileSkill } from "../models/profile";
import { CCLCReq, Opportunity, OpportunityFit } from "../models/opportunity";
import ScoringEngine from "../utils/scoring";
import { Auth } from "../utils/auth";
import Fetcher, { IHttpResult } from "../utils/fetcher";
import { copyArray } from "../utils/data";

export default class ProfileStore {
  private _store: SeekerStore;
  private _se: ScoringEngine;
  @observable profile: Profile;
  @observable editWork: WorkExperience;
  @observable editActivity: ActivityExperience;
  @observable editEducation: EducationExperience;
  @observable editCCLC: CCLCExperience;
  @observable editDemo: Demographics;
  @observable editLanguages: Array<Language>;
  @observable editingLanguages: boolean;
  @observable resumeData: ParsedResumeResults;
  @observable profileViewMode: ProfileViewState;
  @observable skillsViewMode: ProfileSkillState;
  @observable parseStep: number;
  @observable resumeSrc: object;
  @observable dirty: boolean;
  @observable selectedJobs: Array<number>;
  @observable selectedEds: Array<number>;
  @observable selectedLangs: Array<number>;
  @observable suggestedSkills: Array<SkillSuggestion>;
  @observable suggestedPositions: Array<IInMemLookup>;
  @observable suggestedAutocompleteSkills: Array<SkillSuggestion>;
  @observable suggestedCredentials: Array<CCLCReq>;
  @observable saveCt: number;
  @observable experienceType: ExperienceType;
  @observable graphExperience: any;
  @observable selectedSkill: ProfileSkill;
  @observable selectedNewSkill: SkillSuggestion;
  @observable selectedExpType: ExperienceType;
  @observable selectedExpHash: string;
  @observable allExperienceSkills: Array<ExperienceSkill>;
  @observable delExperience: DeleteExperience;
  @observable degreeSelected: boolean;
  @observable dateIsValid: boolean;
  @observable deleteSavedOp: Opportunity;
  @observable addExpRoute: AddExpRoute;
  @observable validationMode: CertValidation;
  @observable refreshAddSkillVw: boolean;

  constructor(store: SeekerStore) {
    this._store = store;
    this._se = new ScoringEngine(this._store);
    this.profile = this.editWork = this.editActivity = this.editEducation = this.editCCLC = this.editDemo = null;
    this.editingLanguages = false;
    this._store.languageCallbacks.push(this.languageReset);
    this.profileViewMode = ProfileViewState.SkillsView;
    this.skillsViewMode = ProfileSkillState.NoSelection;
    this.dirty = false;
    this.resumeData = null;
    this.resumeSrc = null;
    this.parseStep = 1;
    this.selectedJobs = new Array<number>();
    this.selectedEds = new Array<number>();
    this.selectedLangs = new Array<number>();
    this.suggestedSkills = new Array<SkillSuggestion>();
    this.suggestedPositions = new Array<IInMemLookup>();
    this.suggestedAutocompleteSkills = new Array<SkillSuggestion>();
    this.suggestedCredentials = new Array<CCLCReq>();
    this.experienceType = ExperienceType.NotSpecified;
    this.editLanguages = new Array<Language>();
    this.graphExperience = "";
    this.selectedSkill = null;
    this.selectedNewSkill = null;
    this.selectedExpType = ExperienceType.NotSpecified;
    this.selectedExpHash = null;
    this.allExperienceSkills = new Array<ExperienceSkill>();
    this.delExperience = null;
    this.degreeSelected = false;
    this.dateIsValid = false;
    this.deleteSavedOp = null;
    this.addExpRoute = AddExpRoute.All;
    this.validationMode = CertValidation.None;
    this.refreshAddSkillVw = false;
  }

  @action setValidationMode = (mode: CertValidation): void => {
    this.validationMode = mode;
  }

  @action resetValidationMode = (): void => {
    this.validationMode = CertValidation.None;
  }

  @action setRefreshAddSkillVw = (flag: boolean):void => {
    this.refreshAddSkillVw = flag;
  }

  @action clear(): void {
    this.profile = this.editWork = this.editActivity = this.editEducation = this.editCCLC = this.editDemo = null;
  }
  @action setSelectedExperience = (idHash: string, type: ExperienceType): void => {
    this.selectedExpHash = idHash;
    this.selectedExpType = type;
    this.setProfileViewMode(ProfileViewState.ExperienceView);
  }
  languageReset = (): void => {
    if (this.profile) {
      this.loadProfile();
    }
  }
  @action setProfileViewMode = (vw: ProfileViewState): void => {
    this.profileViewMode = vw;
  }

  @action setSkillsViewMode = (vw: ProfileSkillState): void => {
    this.skillsViewMode = vw;
  }

  @action setDeleteSavedOp = (op: Opportunity): void => {
    this.deleteSavedOp = op;
  }

  @action clearDeleteSavedOp = (): void => {
    this.deleteSavedOp = null;
  }

  @action setExpToSkillsId = (id: string): void => {
    this.graphExperience = id;
  }

  @action setSelectedSkill = (skill: ProfileSkill): void => {
    this.selectedSkill = skill;
  }

  clearSelectedSkill = (): void => {
    this.selectedSkill = null;
  }

  clearSelectedProfileSkill = () => {
    this.selectedSkill = null;
  }

  @action setSelectedNewSkill = (skill: SkillSuggestion): void => {
    this.selectedNewSkill = skill;
  }

  @action setDegreeSelected = (selected: boolean): void => {
    this.degreeSelected = selected;
  }

  @action setDateIsValid = (isValid: boolean): void => {
    this.dateIsValid = isValid;
  }

  @action setDeleteExperience(id: string, type: ExperienceType, title: string): void {
    this.delExperience = new DeleteExperience(id, type, title);
  }
  @action clearDeleteExperience = (): void => {
    this.delExperience = null;
  }

  @action setAddExpRoute = (type: AddExpRoute): void => {
    this.addExpRoute = type;
  }

  @action resetAddExpRoute = (): void => {
    this.addExpRoute = AddExpRoute.All;
  }

  @action deleteExperience = (): void => {
    if (this.delExperience.idHash === this.selectedExpHash) {
      this.selectedExpHash = null;
    }
    if (this.delExperience) {  
      let srcType: string = "";
      switch (this.delExperience.type) {
        case ExperienceType.Work:
        case ExperienceType.Military:
          srcType = "jobs";
          const we: WorkExperience = this.profile.jobs.find((w: WorkExperience): boolean => { return w.idHash === this.delExperience.idHash });
          we.skills.map((s: ExperienceSkill): void => {
          this.expDeleteSkillRemoval(s, 0, we, this.delExperience.idHash);
          });
          break;
        case ExperienceType.Course:
        case ExperienceType.GEDDegree:
          srcType = "educations";
          const ee: EducationExperience = this.profile.educations.find((e: EducationExperience): boolean => { return e.idHash === this.delExperience.idHash; });
          ee.skills.map((s: ExperienceSkill): void => {
          this.expDeleteSkillRemoval(s, 0, ee, this.delExperience.idHash);
          });
          break;
        case ExperienceType.Life:
          srcType = "activities";
          const ae: ActivityExperience = this.profile.activities.find((a: ActivityExperience): boolean => { return a.idHash === this.delExperience.idHash; });
          ae.skills.map((s: ExperienceSkill): void => {
          this.expDeleteSkillRemoval(s, 0, ae, this.delExperience.idHash);
          });
          break;
        case ExperienceType.CCLC:
          srcType = "cclcs";
          break;
      }

      Fetcher.deleteWithResult(`api/profile/${srcType}/${this.delExperience.idHash}`, action((resp: IHttpResult): void => {
        if (resp.ok) {
          if (srcType === "jobs") {
            const we: WorkExperience = this.profile.jobs.find((w: WorkExperience): boolean => { return w.idHash === this.delExperience.idHash });
            (this.profile.jobs as IObservableArray<WorkExperience>).remove(we);
          } else if (srcType === "educations") {
            const ee: EducationExperience = this.profile.educations.find((e: EducationExperience): boolean => { return e.idHash === this.delExperience.idHash; });
            (this.profile.educations as IObservableArray<EducationExperience>).remove(ee);
          } else if (srcType === "activities") {
            const ae: ActivityExperience = this.profile.activities.find((a: ActivityExperience): boolean => { return a.idHash === this.delExperience.idHash; });
            (this.profile.activities as IObservableArray<ActivityExperience>).remove(ae);
          } else if (srcType === "cclcs") {
            const cclc: CCLCExperience = this.profile.cclcs.find((c: CCLCExperience): boolean => { return c.idHash === this.delExperience.idHash; });
            (this.profile.cclcs as IObservableArray<CCLCExperience>).remove(cclc);
          }
          this.delExperience = null;
          this._se.rescoreAll();
        }
      }));
    }
  }


  @action loadProfile = (): void => {
    if (this._store.viewState.isBusy(BusyEvent.LoadProfile)) { return; }
    this._store.viewState.setBusyBit(BusyEvent.LoadProfile);
    this._store.viewState.initPath();
    this._store.viewState.initFeaturedContent();
    Fetcher.get("api/profile", action((p: Profile): void => {
      this.profile = new Profile(p);     
      // loading ops for those saved or removing saved ops that don't have a valid opportunity
      this._store.opportunityStore.loadOps(this.profile.savedOps, (): void => {
        this._store.opportunityStore.setFilterZip();
        this._store.opportunityStore.getRecommendedOps();
        this._se.rescoreAll();
        this._store.viewState.clearBusyBit(BusyEvent.LoadProfile);
        this.setAllExperienceSkills();
      });
    }));
  }

  @action setAllExperienceSkills = (): void => {
    this.profile.jobs.map((job: WorkExperience): void => {
      job.skills.map((skill: ExperienceSkill): void => {
        this.allExperienceSkills.push(skill);
      })
    });

    this.profile.educations.map((edu: EducationExperience): void => {
      edu.skills.map((skill: ExperienceSkill): void => {
        this.allExperienceSkills.push(skill);
      })
    });

    this.profile.activities.map((life: ActivityExperience): void => {
      life.skills.map((skill: ExperienceSkill): void => {
        this.allExperienceSkills.push(skill);
      })
    });
  }

  @action selectDemographics = (): void => {
    this.clearEdits();
    this.editDemo = new Demographics(this.profile.demographics);
  }
  @action setEditExperience = (id: string): void => {
    if (this.profile) {
      this.editWork = this.profile.jobs.find((we: WorkExperience): boolean => { return we.idHash === id });
      if (this.editWork) {
        this.experienceType = this.editWork.expType === JobType.Work ? ExperienceType.Work : ExperienceType.Military;
      }
      this.editEducation = this.profile.educations.find((ee: EducationExperience): boolean => { return ee.idHash === id });
      if (this.editEducation) {
        this.experienceType = this.editEducation.edType === EdType.DegreeProgram ? ExperienceType.GEDDegree : ExperienceType.Course;
      }
      this.editActivity = this.profile.activities.find((ae: ActivityExperience): boolean => { return ae.idHash === id });
      if (this.editActivity) {
        this.experienceType = ExperienceType.Life;
      }
      this.editCCLC = this.profile.cclcs.find((c: CCLCExperience): boolean => { return c.idHash === id });
      if (this.editCCLC) {
        this.experienceType = ExperienceType.CCLC;
      }
    }
  }
  @action setEditLanguages = (): Array<Language> => {
    if (this.editLanguages.length > 0) { return this.editLanguages; }
    if (this.resumeData) {
      copyArray(this.resumeData.languages, this.editLanguages, (l: Language) => { return new Language(l); });
    } else if (this.profile && !this.resumeData) {
      const newLangs: Array<Language> = this.editLanguages.filter((l: Language): boolean => { return l.isNew; });
      copyArray(this.profile.languages, this.editLanguages, (l: Language) => { return new Language(l); });
      newLangs.map((l: Language): void => { this.editLanguages.push(l); });
      const editLang: Language = this.editLanguages.find((l: Language): boolean => { return l.langId === 0; });
      if (!editLang) {
        this.editLanguages.push(new Language());
      }
    }
    this.editingLanguages = true;
    return this.editLanguages;
  }
  @action addActivity = (type: ExperienceType): void => {
    this.clearEdits();
    this.experienceType = type;
    switch (type) {
      case ExperienceType.Work:
        this.editWork = new WorkExperience();
        this.editWork.expType = JobType.Work;
        break;
      case ExperienceType.Military:
        this.editWork = new WorkExperience();
        this.editWork.expType = JobType.Military;
        break;
      case ExperienceType.Course:
        this.editEducation = new EducationExperience();
        this.editEducation.edType = EdType.Course;
        break;
      case ExperienceType.CCLC:
        this.editCCLC = new CCLCExperience();
        break;
      case ExperienceType.GEDDegree:
        this.editEducation = new EducationExperience();
        this.editEducation.edType = EdType.DegreeProgram;
        break;
      case ExperienceType.Life:
        this.editActivity = new ActivityExperience();
        break;
    }
  }
  @action selectWork = (id: string): void => {
    this.clearEdits();
    this.editWork = this.selectExperience(this.profile.jobs, id) as WorkExperience;
    this.experienceType = this.editWork.expType === JobType.Military ? ExperienceType.Military : ExperienceType.Work;
  }
  @action selectEducation = (id: string): void => {
    this.clearEdits();
    this.editEducation = this.selectExperience(this.profile.educations, id) as EducationExperience;
    this.experienceType = this.editEducation.edType === EdType.Course ? ExperienceType.Course : ExperienceType.GEDDegree;
  }
  @action selectActivity = (id: string): void => {
    this.clearEdits();
    this.editActivity = this.selectExperience(this.profile.activities, id) as ActivityExperience;
    this.experienceType = ExperienceType.Life;
  }
  @action selectCCLC = (id: string): void => {
    this.editCCLC = this.selectExperience(this.profile.cclcs, id) as CCLCExperience;
    this.experienceType = ExperienceType.CCLC;
  }
  @action deleteLanguage = (l: Language): void => {
    (this.editLanguages as IObservableArray<Language>).remove(l);
  }
  @action deleteLanguageFromSummary = (l: Language): void => {
    (this.profile.languages as IObservableArray<Language>).remove(l);
  }
  @action clearEdits = (): void => {
    this.editWork = this.editActivity = this.editEducation = this.editCCLC = this.editDemo = null;
    (this.editLanguages as IObservableArray<Language>).clear();
    this.experienceType = ExperienceType.NotSpecified;
    this.editingLanguages = false;
    this.setDateIsValid(false);
  }
  private selectExperience = (exp: Array<IExperience>, id: string): IExperience => {
    this.clearEdits()
    const exps: IExperience[] = exp.filter((ex: IExperience): boolean => {
      return ex.idHash === id;
    });
    return exps.length > 0 ? exps[0] : null;
  }
  saveCallback = (): void => {
    this.clearEdits();
    this._se.rescoreAll();
    this._store.opportunityStore.updateRecommendedOps();
  }
  saveExperienceEdits(): void {
    if (this.editWork) {
      this.saveCt = 1;
      this.saveWorkExperience(this.editWork, this.saveCallback);
    } else if (this.editEducation) {
      this.saveCt = 1;
      this.saveEducationExperience(this.editEducation, this.saveCallback);
    } else if (this.editCCLC) {
      this.setDateIsValid(false); //changes the validation rule back 
      this.saveCt = 1;
      this.saveCCLC(this.editCCLC, this.saveCallback);
    } else if (this.editActivity) {
      this.saveCt = 1;
      this.saveLifeExperience(this.editActivity, this.saveCallback);
    } else if (this.editingLanguages) {
      this.saveCt = 1;
      this.saveLanguages(this.editLanguages, this.clearEdits);
    }
  }
  // Demographics functions
  @action initDemographicEdits() {
    this.editDemo = new Demographics(this.profile.demographics);
  }
  @action clearDemographicEdits() {
    this.editDemo = null;
  }
  @action updateDemographicsRegion = (key: string, value: string, ndx: number): void => {
    if (ndx === -1) {
      const r: string = (key === "regionIdHash") ? value : "";
      const s: string = (key === "sourceIdHash") ? value : "";
      this.editDemo.referrals.push(new Referral({ regionIdHash: r, sourceIdHash: s }));
      this.dirty = true;
    } else {
      if (this.editDemo.referrals[ndx][key] !== value) {
        this.editDemo.referrals[ndx][key] = value;
        if (key === "regionIdHash") {
          this.editDemo.referrals[ndx].sourceIdHash = EmptyId;
        }
        this.dirty = true;
      }
    }
  }
  @action updateDemographics = (evt: any): void => {
    const key: string = evt.target.id || evt.target.name;
    if (evt.target && key) {
      const tokens: string[] = key.split(".");
      if (tokens.length === 2 && tokens[0] === "address") {
        if (this.editDemo.address[tokens[1]] !== evt.target.value) {
          this.editDemo.address[tokens[1]] = evt.target.value;
          this.dirty = true;
        }
      } else {
        if (this.editDemo[tokens[0]] !== evt.target.value) {
          this.editDemo[tokens[0]] = evt.target.value;
          this.dirty = true;
        }
      }
    }
  }
  @action updateDemographicsDOB = (dt: Date): void => {
    this.editDemo.dob = dt;
    /*
  try {
    const dt: Date = new Date(e.target.value);
    if (!isNaN(dt.valueOf())) {
      this.editDemo.dob = dt;
      this.dirty = true;
    }
  } catch {}*/
  }
  @action updateDemographicsOptIn = (optIn: boolean): void => {
    this.editDemo.contactFlag = optIn;
    this.editDemo.contact = optIn ? 1 : 0;
    this.dirty = true;
  }
  @action resetDemographics = (): void => {
    this.editDemo = new Demographics(this.profile.demographics);
    this.dirty = false;
  }
  @action saveDemographics = (): void => {
    Fetcher.postWithResult("api/profile/demographics", this.editDemo, action((resp: IHttpResult): void => {
      if (resp.ok) {
        this.profile.demographics = new Demographics(this.editDemo);
        this._store.viewState.setShowSnackbar(true, this._store.localization.fields.demographics.savedData);
        this.dirty = false;
      } else
        this._store.viewState.setShowSnackbar(true, this._store.localization.fields.demographics.savedError);
    }));
  }
  // Suggested Positions
  getSuggestedPositions = (text: string): void => {
    Fetcher.get(`api/positions/suggestions?text=${text}`, action((positions: Array<IInMemLookup>): void => {
      (this.suggestedPositions as IObservableArray<IInMemLookup>).replace(positions);
    }));
  }

  // Suggested Skills for Autocomplete 
  @action getAutoCompleteSkills = (text: string): void => {
    Fetcher.get(`api/skills/suggestions/freeform?text=${text}`, action((skill: Array<SkillSuggestion>): void => {
      copyArray(skill, this.suggestedAutocompleteSkills, (ss: SkillSuggestion) => { return new SkillSuggestion(ss); });
    }));
  }

  //clear Suggested autocomplete Skills
  @action clearSuggestedAutocompleteSkills() {
    (this.suggestedAutocompleteSkills as IObservableArray<SkillSuggestion>).clear();
  }

  @action clearSuggestedPositions = (): void => {
    (this.suggestedPositions as IObservableArray<IInMemLookup>).clear();
  }
  @action selectPosition = (pos: IInMemLookup): void => {
    this.editWork.refPositionId = pos.key;
    this.editWork.position = pos.value;
    (this.suggestedPositions as IObservableArray<IInMemLookup>).clear();
    this.getSuggestedSkills(null, pos.key);
  }
  // Suggested Skills
  @action clearSuggestedSkills() {
    (this.suggestedSkills as IObservableArray<SkillSuggestion>).clear();
  }
  getSkillExperience = (skillId: string): ExperienceSkill => {
    let src: ISkillSource = null;
    if (this.editWork) {
      src = this.editWork;
    } else if (this.resumeSrc) {
      src = this.resumeSrc as ISkillSource;
    }
    if (src) {
      return src.skills.find((es: ExperienceSkill): boolean => {
        return es.skillId === skillId;
      });
    }
    return null;
  }
  // Lookups up skills either by a position title, or if we have a mapped id for the position by Id/type 
  @action getSuggestedSkills(position?: string, id?: string): void {
    const url: string = position ? `api/skills/suggestions?position=${position}` : `/api/skills/suggestions/positions/${id}`;
    Fetcher.get(url, action((skills: Array<SkillSuggestion>): void => {
      copyArray(skills, this.suggestedSkills, (ss: SkillSuggestion) => { return new SkillSuggestion(ss); });
      let src: ISkillSource = null;
      if (this.editWork) {
        src = this.editWork;
      } else if (this.resumeSrc) {
        src = this.resumeSrc as ISkillSource;
      }
      if (src) {
        this.suggestedSkills.map((s: SkillSuggestion): void => {
          let exp: ExperienceSkill = this.getSkillExperience(s.id);
          if (!exp) {
            exp = new ExperienceSkill();
            exp.skillId = s.id;
            exp.used = 0;
            src.skills.push(exp);
          }
        });
      }
    }));
  }
  @action setSkillUsage = (skillId: string, usage: number): void => {
    const exp: ExperienceSkill = this.getSkillExperience(skillId);
    let psk: ProfileSkill = null;
    if (exp) {
      exp.used = usage;
      psk = this.profile.skills.find((ps: ProfileSkill): boolean => {
        return ps.idHash === exp.skillId;
      });
      if (!psk) {
        Fetcher.get(`api/skills/${exp.skillId}`, action((sk: ProfileSkill): void => {
          this.profile.skills.push(new ProfileSkill(sk));
        }))
      }
    }
  }
  getSkillSource(expId: string): ISkillSource {
    if (!this.profile) {
      return null;
    }
    let src: ISkillSource = this.profile.jobs.find((we: WorkExperience): boolean => {
      return we.idHash === expId;
    });
    if (!src) {
      src = this.profile.educations.find((ee: EducationExperience): boolean => {
        return ee.idHash === expId;
      });
    }
    if (!src) {
      src = this.profile.activities.find((ae: ActivityExperience): boolean => {
        return ae.idHash === expId;
      });
    }
    return src;
  }
  private getSourceType(exp: ISkillSource): string {
    if (exp instanceof WorkExperience) {
      return "jobs";
    } else if (exp instanceof EducationExperience) {
      return "educations";
    } else if (exp instanceof ActivityExperience) {
      return "activities";
    }
    return "";
  }

  @action expDeleteSkillRemoval = (sk: ExperienceSkill, usage: number, exp: ISkillSource, expId: string): void => {
    if (sk) {
      sk.used = usage;
      if (sk.used === 0) {
        exp.skills.map((es: ExperienceSkill, n: number): void => {
          if (es.skillId === sk.skillId) {
            exp.skills.splice(n, 1);
          }
        })
        if (!this.checkSkillRemoval(sk, expId)) {        
          this.profile.skills = this.profile.skills.filter((ps: ProfileSkill): boolean => { return ps.idHash !== sk.skillId });
        }
      }
    } 

  }
  @action updateSkillUsage = (expId: string, skillId: string, usage: number): void => {
    const exp: ISkillSource = this.getSkillSource(expId);
    const srcType: string = this.getSourceType(exp);

    if (!srcType) { return; }
    let sk: ExperienceSkill = null;
    let psk: ProfileSkill = null;
    if (exp) {
      sk = exp.skills.find((es: ExperienceSkill): boolean => {
        return es.skillId === skillId;
      });
      if (sk) {
        sk.used = usage;
        if (sk.used === 0) {
          exp.skills.map((es: ExperienceSkill, n: number): void => {
            if (es.skillId === sk.skillId) {
              exp.skills.splice(n, 1);
            }
          })
          if (!this.checkSkillRemoval(sk, expId)) {
            this.profile.skills = this.profile.skills.filter((ps: ProfileSkill): boolean => { return ps.idHash !== sk.skillId });
          }
        }
      } else {
        sk = new ExperienceSkill();
        sk.skillId = skillId;
        sk.used = usage;
        psk = this.profile.skills.find((ps: ProfileSkill): boolean => {
          return ps.idHash === skillId;
        });
        if (!psk) {
          Fetcher.get(`api/skills/${sk.skillId}`, action((sk: ProfileSkill): void => {
            this.profile.skills.push(new ProfileSkill(sk));
          }))
        }
        exp.skills.push(sk);
      }
      Fetcher.postWithResult(`api/profile/${srcType}`, exp, (result: IHttpResult): void => {
        if (result.ok) {
          this._se.rescore(skillId);
        }
      });
      this._store.viewState.setShowSnackbar(true, this._store.localization.fields.skillUpdatedMsg);
    }
  }

  /* when setting a skill usage to none - check whether or not the skill exists within another experience */
  checkSkillRemoval = (sk: ExperienceSkill, exp: string): boolean => {
    let flag: boolean = false;

    this.profile.jobs.map((jobs: WorkExperience): void => {
      jobs.skills.map((jobSkill: ExperienceSkill): void => {
        if (jobSkill.skillId === sk.skillId && jobs.idHash !== exp) {
          flag = true;
          return;
        }
      })
    });

    this.profile.educations.map((edu: EducationExperience): void => {
      edu.skills.map((eduSkill: ExperienceSkill): void => {
        if (eduSkill.skillId === sk.skillId && edu.idHash !== exp) {
          flag = true;
          return;
        }
      })
    });

    this.profile.activities.map((act: ActivityExperience): void => {
      act.skills.map((actSkill: ExperienceSkill): void => {
        if (actSkill.skillId === sk.skillId && act.idHash !== exp) {
          flag = true;
          return;
        }
      })
    });
    return flag;
  }

  // Resume mapping functions
  @action resetParsing = (): void => {
    this._store.viewState.clearBusyBit(BusyEvent.ParseResume);
    this.parseStep = 1;
    this.resumeData = null;
    this.resumeSrc = null;
    this.selectedJobs = new Array<number>();
    this.selectedEds = new Array<number>();
    this.selectedLangs = new Array<number>();
  }
  @action private setResumeSrc(): void {
    this.clearSuggestedSkills();
    const ndx = this.parseStep - 2;
    if (ndx < 0) {
      this.resumeSrc = null;
    } else if (ndx < this.resumeData.jobs.length) {
      this.resumeSrc = this.resumeData.jobs[ndx];
      if (this.resumeData.jobs[ndx].expType === JobType.Work) {
        this.getSuggestedSkills(this.resumeData.jobs[ndx].position);
      }
    } else if (ndx < (this.resumeData.jobs.length + this.resumeData.educations.length)) {
      this.resumeSrc = this.resumeData.educations[ndx - this.resumeData.jobs.length];
    } else if (ndx < (this.resumeData.jobs.length + this.resumeData.educations.length + this.resumeData.languages.length)) {
      this.resumeSrc = this.resumeData.languages; // this.resumeData.languages[ndx - this.resumeData.jobs.length - this.resumeData.educations.length];
    } else {
      this.resumeSrc = null;
    }
  }
  @action incrementParseStep = (): void => {
    if (this.parseStep === 2 + this.resumeData.jobs.length + this.resumeData.educations.length) {
      this.parseStep = 2 + this.resumeData.jobs.length + this.resumeData.educations.length + this.resumeData.languages.length;
    } else {
      this.parseStep++;
    }
    this.setResumeSrc();
  }
  @action decrementParseStep = (): void => {
    if (this.parseStep === 2 + this.resumeData.jobs.length + this.resumeData.educations.length + this.resumeData.languages.length) {
      this.parseStep -= this.resumeData.languages.length;
    } else {
      this.parseStep--;
    }
    this.setResumeSrc();
  }
  @action uploadResume = (file: File): void => {
    this._store.viewState.setBusyBit(BusyEvent.ParseResume);
    this.resumeData = null;
    Fetcher.postResume([file], action((prr: ParsedResumeResults): void => {
      this.resumeData = new ParsedResumeResults(prr);
      for (let i: number = 0; i < this.resumeData.jobs.length; ++i) {
        this.selectedJobs.push(i);
      }
      for (let i: number = 0; i < this.resumeData.educations.length; ++i) {
        this.selectedEds.push(i);
      }
      for (let i: number = 0; i < this.resumeData.languages.length; ++i) {
        this.selectedLangs.push(i);
      }
      this.profile.resumeDoc = this.resumeData.name;
      this.profile.resumeId = this.resumeData.resumeId;
      const dataFound: boolean = this.selectedJobs.length > 0 || this.selectedEds.length > 0 || this.selectedLangs.length > 0;
      if (this.resumeData.upload && dataFound) {
        this.profile.resumeUpload = new Date(this.resumeData.upload.valueOf());
      }
      this._store.viewState.clearBusyBit(BusyEvent.ParseResume);
    }));
  }
  // Keeps track of the parsed data indices to add to the user's profile
  private getSource(src: string): Array<number> {
    let arr: Array<number> = null;
    switch (src) {
      case "job":
        arr = this.selectedJobs;
        break;
      case "ed":
        arr = this.selectedEds;
        break;
      case "lang":
        arr = this.selectedLangs;
        break;
    }
    return arr;
  }
  private getIndex(src: string): number {
    switch (src) {
      case "job":
        return this.parseStep - 2;
      case "ed":
        return this.parseStep - 2 - this.resumeData.jobs.length;
      case "lang":
        return this.parseStep - 2 - this.resumeData.jobs.length - this.resumeData.educations.length;
    }
    return -1;
  }
  srcIncluded = (src: string): boolean => {
    let arr: Array<number> = this.getSource(src);
    return arr && arr.indexOf(this.getIndex(src)) !== -1;
  }
  @action setSelected = (src: string, chk: boolean): void => {
    const arr: Array<number> = this.getSource(src);
    const i: number = this.getIndex(src);
    if (arr) {
      if (chk) {
        arr.push(i);
      } else {
        (arr as IObservableArray<number>).remove(i);
      }
    }
  }
  languageSelected = (l: Language): boolean => {
    if (this.resumeData) {
      const ndx: number = this.resumeData.languages.findIndex((lang: Language): boolean => { return l.langId === lang.langId });
      return this.selectedLangs.indexOf(ndx) !== -1;
    }
    else {
      return this.profile.languages.findIndex((lang: Language): boolean => { return l.langId === lang.langId }) != -1;
    }
  }
  @action setProficiency = (l: Language, flag: number, chk: boolean): void => {
    if (chk) {
      l.proficiency += flag;
    } else {
      l.proficiency -= flag;
    }
  }
  @action includeLanguage = (l: Language, chk: boolean): void => {
    let ndx: number = -1;
    if (this.resumeData) {
      ndx = this.editLanguages.findIndex((lang: Language): boolean => { return l === lang; });
      if (ndx === -1) {
        return;
      }
      if (chk) {
        this.selectedLangs.push(ndx);
      } else {
        (this.selectedLangs as IObservableArray<number>).remove(ndx);
      }
    }
  }
  resumeImportCallback = (): void => {
    this.loadProfile();
    this.resetParsing();
    hashHistory.push("/profile");
  }
  @action saveResumeImport = (): void => {
    this.saveCt = this.selectedJobs.length + this.selectedEds.length;
    if (this.selectedLangs.length > 0) {
      this.saveCt++;
    }
    this.selectedJobs.map((ndx: number): void => {
      this.saveWorkExperience(this.resumeData.jobs[ndx], this.resumeImportCallback);
    });
    this.selectedEds.map((ndx: number): void => {
      this.saveEducationExperience(this.resumeData.educations[ndx], this.resumeImportCallback);
    });
    if (this.selectedLangs.length > 0) {
      this.saveLanguages(this.editLanguages, this.resumeImportCallback);
    }

    if (this.saveCt === 0) { hashHistory.push("/profile"); }
  }
  @action clearUnusedSkills(ss: ISkillSource): void {
    ss.skills.filter((es: ExperienceSkill): boolean => {
      return es.used === 0
    }).map((es: ExperienceSkill): void => {
      (ss.skills as IObservableArray<ExperienceSkill>).remove(es);
    });
  }
  @action private handleSaveCB(saveCB?: () => void) {
    if (saveCB) {
      this.saveCt--;
      if (this.saveCt === 0) {
        saveCB();
      }
    }
  }
  @action saveLanguages = (langs: Language[], saveCB?: () => void): void => {
    Fetcher.put("api/profile/languages", langs, action((success: boolean): void => {
      if (success) {
        copyArray(langs, this.profile.languages, (l: Language) => { return new Language(l); });
        this.handleSaveCB(saveCB);
      }
    }));
  }
  @action saveWorkExperience = (we: WorkExperience, saveCB?: () => void): void => {
    this.clearUnusedSkills(we);
    Fetcher.postWithResult("api/profile/jobs", we, action((resp: IHttpResult): void => {
      if (resp.ok) {
        if (we.idHash === EmptyId) {
          we.idHash = resp.id;
          this.profile.jobs.push(we);
        } else {
          const ndx: number = this.profile.jobs.findIndex((we: WorkExperience): boolean => {
            return we.idHash === resp.id;
          });
          if (ndx !== -1) {
            this.profile.jobs[ndx] = new WorkExperience(we);
          }
        }
        this.handleSaveCB(saveCB);
      }
    }));
  }
  @action saveEducationExperience = (ee: EducationExperience, saveCB?: () => void): void => {
    this.clearUnusedSkills(ee);
    Fetcher.postWithResult("api/profile/educations", ee, action((resp: IHttpResult): void => {
      if (resp.ok) {
        if (ee.idHash === EmptyId) {
          ee.idHash = resp.id;
          this.profile.educations.push(ee);
        } else {
          const ndx: number = this.profile.educations.findIndex((ee: EducationExperience): boolean => {
            return ee.idHash === resp.id;
          });
          if (ndx !== -1) {
            this.profile.educations[ndx] = new EducationExperience(ee);
          }
        }
        this.handleSaveCB(saveCB);
      }
    }));
  }
  @action saveLifeExperience = (ae: ActivityExperience, saveCB?: () => void): void => {
    this.clearUnusedSkills(ae);
    Fetcher.postWithResult("api/profile/activities", ae, action((resp: IHttpResult): void => {
      if (resp.ok) {
        if (ae.idHash === EmptyId) {
          ae.idHash = resp.id;
          this.profile.activities.push(ae);
        } else {
          const ndx: number = this.profile.activities.findIndex((ae: ActivityExperience): boolean => {
            return ae.idHash === resp.id;
          });
          if (ndx !== -1) {
            this.profile.activities[ndx] = new ActivityExperience(ae);
          }
        }
        this.handleSaveCB(saveCB);
      }
    }));
  }
  @action saveCCLC = (cclc: CCLCExperience, saveCB?: () => void): void => {
    Fetcher.postWithResult("api/profile/cclcs", cclc, action((resp: IHttpResult): void => {
      if (resp.ok) {
        if (cclc.idHash === EmptyId) {
          cclc.idHash = resp.id;
          this.profile.cclcs.push(cclc);
        } else {
          const ndx: number = this.profile.cclcs.findIndex((ce: CCLCExperience): boolean => {
            return ce.idHash === resp.id;
          });
          if (ndx !== -1) {
            this.profile.cclcs[ndx] = new CCLCExperience(cclc);
          }
        }
        this.handleSaveCB(saveCB);
      }
    }));
  }
  @action clearSuggestedCredentials = (): void => {
    (this.suggestedCredentials as IObservableArray<CCLCReq>).clear();
  }
  @action getSuggestedCredentials(title: string, autoSelect: boolean): void {
    const url: string = `api/cclcs/suggestions?text=${title}`;
    Fetcher.get(url, action((creds: Array<CCLCReq>): void => {
      copyArray(creds, this.suggestedCredentials, (cred: CCLCReq) => { return new CCLCReq(cred); });
      if (creds.length === 1 && autoSelect && this.editCCLC) {
        this.editCCLC.cclcId = creds[0].idHash;
        this.editCCLC.title = creds[0].title;
        this.editCCLC.type = creds[0].type;
        this.editCCLC.cclcRef = creds[0];
        this.clearSuggestedCredentials();
      }
    }));
  }
  getSavedOpportunity = (opId: string): SavedOpportunity => {
    if (this.profile) {
      return this.profile.savedOps.find((so: SavedOpportunity): boolean => {
        return so.opportunityId === opId;
      });
    } else {
      return null;
    }
  }
  private addSavedOpportunity(op: Opportunity, opFit: OpportunityFit): void {
    if (this.profile && op && opFit) {
      let savedOp: SavedOpportunity = new SavedOpportunity();
      savedOp.mapFromFit(opFit);
      this.profile.savedOps.push(savedOp);
      this._se.scoreOpportunity(op, savedOp);
    }
  }
  updateSavedOpportunityState(opId: string, state: SavedOpportunityState, add: boolean): void {
    const so: SavedOpportunity = this.getSavedOpportunity(opId);
    if (add && (so.status & state) === 0) {
      so.status += state;
    } else if (!add && (so.status & state) > 0) {
      so.status -= state;
    }
  }
  watchOpportunity = (op: Opportunity): void => {
    Fetcher.putWithData(`api/saved/${op.idHash}`, {}, action((opFit: OpportunityFit): void => {
      const savedOp: SavedOpportunity = this.profile.savedOps.find((so: SavedOpportunity): boolean => {
        return so.opportunityId === op.idHash;
      });
      if (savedOp && (savedOp.status & SavedOpportunityState.Saved) === 0) {
        savedOp.status += SavedOpportunityState.Saved;
      } else {
        this.addSavedOpportunity(op, opFit);
      }
    }));
  }
  unwatchOpportunity = (op: Opportunity): void => {
    Fetcher.delete(`api/saved/${op.idHash}`, action((success: boolean): void => {
      if (success) {
        const savedOp: SavedOpportunity = this.profile.savedOps.find((so: SavedOpportunity): boolean => {
          return so.opportunityId === op.idHash;
        });
        if ((savedOp.status & SavedOpportunityState.Saved) > 0) {
          savedOp.status -= SavedOpportunityState.Saved;
        }
        this.clearDeleteSavedOp();
      }
    }));
  }
  @action addSelfCert = (certId: string): void => {
    if (this.profile) {
      this.profile.selfCerts.push(certId);
      this.saveSelfCerts();
    }
  };
  @action deleteSelfCert = (certId: string): void => {
    if (this.profile) {
      (this.profile.selfCerts as IObservableArray<string>).remove(certId);
      this.saveSelfCerts();
    }
  };
  saveSelfCerts = (): void => {
    Fetcher.put(`api/profile/selfcerts`, this.profile.selfCerts, action((success: boolean): void => {

    }));
  };
  trackOpportunity = (op: Opportunity): void => {
    // No need to track if we already do.
    if (!this.profile || (this.profile && this.profile.savedOps.find((so: SavedOpportunity): boolean => {
      return so.opportunityId === op.idHash;
    }))) {
      return;
    }
    Fetcher.putWithData(`api/viewed/${op.idHash}`, {}, action((opFit: OpportunityFit): void => {
      this.addSavedOpportunity(op, opFit);
    }));
  }
  getNonOpProfile = (): void => {
    this._store.viewState.setBusyBit(BusyEvent.GeneratingNonSpecificProfile);
    if (Auth.isAuthenticated) {
      Fetcher.getFileDownload("api/opportunities/non-op-profile", (): void => {
        this._store.viewState.clearBusyBit(BusyEvent.GeneratingNonSpecificProfile);
      } );
    }    
  }


}


// WEBPACK FOOTER //
// ./src/stores/profileStore.ts